<script lang="ts" setup>
import { ElNotification, ElPopconfirm, type FormInstance, type FormRules } from 'element-plus'
import { computed, reactive, ref, watch } from 'vue'

import ActivityAvatar from './ActivityAvatar.vue'

import { useUpdateCampaignParticipant } from '@/api/campaignParticipant.api'
import SystemIcon from '@/assets/icons/settings.svg?component'
import { getParticipantAvatarAbbreviation } from '@/components/CampaignParticipants/CampaignParticipantDetails/CampaignParticipantDetails.helpers'
import { formatDateTimeLong } from '@/lib/formatters'
import type { ActivityCommentType, AvatarDataType } from '@/types/campaignParticipant.types'
import { RichTextEditor } from '@/ui-library'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const props = defineProps<{
  campaignId: number
  participantId: number | string
  comment: ActivityCommentType
  avatarData: AvatarDataType
  isAuthor: boolean
  index: number
  onDelete: () => void
}>()
const { isLoading, editComment, deleteComment } = useUpdateCampaignParticipant(props.campaignId)

const isEditState = ref(false)
const formRef = ref<FormInstance>()
const formData = reactive({ comment: props.comment.comment_text })
const rules = reactive<FormRules<{ comment: string }>>({
  comment: [
    { max: 15000, message: 'Field must be shorter than 15000 symbols' },
    { required: true, message: 'This field is required' },
  ],
})
const createdDate = computed(() => formatDateTimeLong(props.comment.created_at))
const editedDate = computed(() => (props.comment.updated_at ? formatDateTimeLong(props.comment.updated_at) : null))
const avatarColor = computed(() => (props.index % 2 === 0 ? '#afecc4' : '#ecafaf'))
const avatarAbbreviation = computed(() => getParticipantAvatarAbbreviation(props.comment.actor_display_name))

watch(
  () => props.comment,
  (value, oldValue) => {
    if (value !== oldValue) {
      formData.comment = value.comment_text
    }
  },
)
const handleSubmit = (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }

  return formEl.validate(async (valid) => {
    if (valid) {
      await editComment(props.participantId, props.comment.comment_id, formData.comment)
      ElNotification.success('Comment was successfully changed.')
      isEditState.value = false
    }
  })
}
const handleReset = (formEl: FormInstance | undefined) => {
  isEditState.value = false
  if (!formEl) {
    return
  }
  formEl.resetFields(['comment'])
}
const onDeleteComment = async (commentId: number) => {
  await deleteComment(props.participantId, commentId)
  props.onDelete()
  ElNotification.success('Comment was successfully deleted.')
}
</script>
<template>
  <FlexComponent :gap="8">
    <ActivityAvatar
      v-if="comment.actor_display_name"
      :abbreviation="avatarAbbreviation"
      :color="avatarColor"
      class="participant-details-comment__avatar"
    />
    <ActivityAvatar v-else :icon="SystemIcon" color="#E0E4EA" class="participant-details-comment__avatar" />
    <FlexComponent :gap="4" direction="column" class="w-100">
      <BoxComponent
        w="100%"
        class="participant-details-comment__title"
        :class="{ 'participant-details-comment__title--new': comment.has_unread_notification }"
      >
        <FlexComponent :gap="8" align="center">
          <TypographyComponent variant="title-medium">{{ comment.actor_display_name || '' }}</TypographyComponent>
          <TypographyComponent variant="body-small" color="#252423">
            {{ createdDate }}
            <el-popover
              v-if="editedDate"
              width="auto"
              trigger="hover"
              placement="bottom-start"
              :teleported="false"
              :show-arrow="false"
              popper-class="participant-details-content__icon-popover"
            >
              <BoxComponent :pl="16" :pr="16">
                <TypographyComponent variant="body-medium"> Edited {{ editedDate }} </TypographyComponent>
              </BoxComponent>
              <template #reference> (edited) </template>
            </el-popover>
          </TypographyComponent>
        </FlexComponent>
      </BoxComponent>
      <template v-if="!isEditState">
        <FlexComponent class="participant-details-comment" align="center">
          <TypographyComponent is="div" variant="body-large" color="#252423">
            <div class="participant-details-comment__content" v-html="formData.comment" />
          </TypographyComponent>
        </FlexComponent>
        <FlexComponent v-if="isAuthor" :gap="4">
          <TypographyComponent
            variant="body-medium"
            class="participant-details-comment__action"
            :font-weight="500"
            @click="isEditState = true"
          >
            Edit
          </TypographyComponent>
          <TypographyComponent variant="body-medium" :font-weight="500" class="participant-details-comment__action">
            ·
          </TypographyComponent>
          <ElPopconfirm
            :teleported="false"
            :width="360"
            title="Are you sure to delete this comment?"
            @confirm="onDeleteComment(comment.comment_id)"
          >
            <template #reference>
              <TypographyComponent variant="body-medium" class="participant-details-comment__action" :font-weight="500">
                Delete
              </TypographyComponent>
            </template>
          </ElPopconfirm>
        </FlexComponent>
      </template>
      <el-form v-else ref="formRef" :model="formData" :rules="rules" @submit.prevent="handleSubmit(formRef)">
        <el-form-item prop="comment" style="margin-bottom: 8px">
          <RichTextEditor v-model="formData.comment" placeholder="Write a comment" />
        </el-form-item>
        <FlexComponent :gap="8">
          <ButtonComponent variant="accent" :is-disabled="isLoading" type="submit">Save</ButtonComponent>
          <el-button round :disabled="isLoading" @click="handleReset(formRef)">Cancel</el-button>
        </FlexComponent>
      </el-form>
    </FlexComponent>
  </FlexComponent>
</template>
<style lang="scss" scoped>
:deep(.el-popconfirm__action) {
  button {
    border-radius: 40px;
  }
}
h5 {
  margin-bottom: 0;
}
.participant-details-comment {
  border-radius: 8px;
  padding: 15px 16px;
  min-height: 54px;
  border: 1px solid rgba(3, 30, 81, 0.12);
}
:deep(.participant-details-comment__content) {
  a {
    text-decoration: underline;
  }

  p,
  ul,
  ol {
    margin-bottom: 15px;
    padding-bottom: 1px;
  }

  p:last-child,
  ul:last-child,
  ol:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }
}
.participant-details-comment__action {
  cursor: pointer;
}

.participant-details-comment__avatar {
  flex: 0 0 32px;
}
.participant-details-comment__title {
  position: relative;
}

.participant-details-comment__title--new {
  padding-right: 20px;

  :before {
    content: '';
    position: absolute;
    right: 2px;
    top: 2px;
    width: 8px;
    height: 8px;
    background: #ea4f85;
    border-radius: 50%;
  }
}
</style>
